import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import * as fabric from "fabric";
import { QRCodeCanvas } from "qrcode.react";
import jsPDF from "jspdf";
import api from "@/lib/api";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import { Type, Image as ImgIcon, Square, Circle as CircleIcon, QrCode, Undo2, Redo2, Save, Layers, Download, AlignLeft, AlignCenter, AlignRight, Trash2 } from "lucide-react";

const BLEED_PX = 18, SAFE_PX = 36;

export default function Designer() {
  const { id: existingId } = useParams();
  const [params] = useSearchParams();
  const productSlug = params.get("product") || "premium-business-cards";
  const canvasRef = useRef(null);
  const fabRef = useRef(null);
  const historyRef = useRef({ stack: [], idx: -1, restoring: false });
  const [product, setProduct] = useState(null);
  const [designName, setDesignName] = useState("Untitled Design");
  const [designId, setDesignId] = useState(existingId || null);
  const [size, setSize] = useState({ w: 1050, h: 600 }); // 3.5x2in @ 300dpi
  const [bg, setBg] = useState("#ffffff");
  const [selected, setSelected] = useState(null);
  const [qrText, setQrText] = useState("https://bolawpress.com");
  const [saving, setSaving] = useState(false);
  const nav = useNavigate();

  const pushHistory = useCallback(() => {
    if (!fabRef.current || historyRef.current.restoring) return;
    const json = JSON.stringify(fabRef.current.toJSON());
    const h = historyRef.current;
    h.stack = h.stack.slice(0, h.idx + 1); h.stack.push(json); h.idx = h.stack.length - 1;
    if (h.stack.length > 40) { h.stack.shift(); h.idx--; }
  }, []);

  useEffect(() => {
    api.get(`/products/${productSlug}`).then(r => setProduct(r.data)).catch(e => logger.error(e));
  }, [productSlug]);

  useEffect(() => {
    if (!canvasRef.current) return;
    const c = new fabric.Canvas(canvasRef.current, { width: size.w, height: size.h, backgroundColor: bg, preserveObjectStacking: true });
    fabRef.current = c;
    c.on("object:modified", pushHistory);
    c.on("object:added", pushHistory);
    c.on("object:removed", pushHistory);
    c.on("selection:created", (e) => setSelected(e.selected?.[0] || null));
    c.on("selection:updated", (e) => setSelected(e.selected?.[0] || null));
    c.on("selection:cleared", () => setSelected(null));
    // Load existing design
    if (existingId) {
      api.get(`/designs/${existingId}`).then(r => {
        setDesignName(r.data.name); setSize({ w: r.data.width_px, h: r.data.height_px });
        setBg(r.data.canvas_json?.background || "#ffffff");
        if (r.data.canvas_json && Object.keys(r.data.canvas_json).length > 0) {
          historyRef.current.restoring = true;
          c.loadFromJSON(r.data.canvas_json, () => { c.renderAll(); historyRef.current.restoring = false; pushHistory(); });
        } else { pushHistory(); }
      }).catch(e => logger.error(e));
    } else { pushHistory(); }
    return () => { c.dispose(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (fabRef.current) { fabRef.current.setDimensions({ width: size.w, height: size.h }); fabRef.current.renderAll(); } }, [size]);
  useEffect(() => { if (fabRef.current) { fabRef.current.backgroundColor = bg; fabRef.current.renderAll(); } }, [bg]);

  const addText = () => {
    const t = new fabric.Textbox("Your Text Here", { left: 100, top: 100, fontSize: 48, fontFamily: "Cormorant Garamond, serif", fill: "#0a0a0a", width: 400 });
    fabRef.current.add(t); fabRef.current.setActiveObject(t);
  };
  const addRect = () => {
    const r = new fabric.Rect({ left: 150, top: 150, width: 200, height: 120, fill: "#C5A059" });
    fabRef.current.add(r); fabRef.current.setActiveObject(r);
  };
  const addCircle = () => {
    const c = new fabric.Circle({ left: 200, top: 200, radius: 60, fill: "#0a0a0a" });
    fabRef.current.add(c); fabRef.current.setActiveObject(c);
  };
  const addImage = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const img = await fabric.FabricImage.fromURL(ev.target.result);
      img.scaleToWidth(400);
      fabRef.current.add(img); fabRef.current.setActiveObject(img);
    };
    reader.readAsDataURL(file);
  };
  const addQr = async () => {
    // Render QR to a hidden canvas, then insert as image
    const tmp = document.createElement("canvas"); tmp.width = 200; tmp.height = 200;
    const container = document.getElementById("qr-tmp");
    if (!container) return toast.error("QR tmp not ready");
    const data = container.querySelector("canvas")?.toDataURL("image/png");
    if (!data) return toast.error("QR not ready");
    const img = await fabric.FabricImage.fromURL(data);
    img.scaleToWidth(180);
    fabRef.current.add(img); fabRef.current.setActiveObject(img);
  };
  const undo = () => {
    const h = historyRef.current;
    if (h.idx <= 0) return;
    h.idx--; h.restoring = true;
    fabRef.current.loadFromJSON(JSON.parse(h.stack[h.idx]), () => { fabRef.current.renderAll(); h.restoring = false; });
  };
  const redo = () => {
    const h = historyRef.current;
    if (h.idx >= h.stack.length - 1) return;
    h.idx++; h.restoring = true;
    fabRef.current.loadFromJSON(JSON.parse(h.stack[h.idx]), () => { fabRef.current.renderAll(); h.restoring = false; });
  };
  const del = () => { const o = fabRef.current.getActiveObject(); if (o) fabRef.current.remove(o); };
  const bringForward = () => { const o = fabRef.current.getActiveObject(); if (o) fabRef.current.bringObjectForward(o); };
  const sendBackward = () => { const o = fabRef.current.getActiveObject(); if (o) fabRef.current.sendObjectBackwards(o); };
  const align = (h) => {
    const o = fabRef.current.getActiveObject(); if (!o) return;
    const cw = fabRef.current.getWidth();
    if (h === "left") o.set({ left: BLEED_PX });
    else if (h === "center") o.set({ left: (cw - (o.width * o.scaleX)) / 2 });
    else if (h === "right") o.set({ left: cw - (o.width * o.scaleX) - BLEED_PX });
    fabRef.current.renderAll(); pushHistory();
  };
  const save = async () => {
    setSaving(true);
    try {
      const json = fabRef.current.toJSON();
      json.background = bg;
      const preview = fabRef.current.toDataURL({ format: "png", multiplier: 0.5 });
      const payload = { name: designName, product_id: product?.id, canvas_json: json, preview_image: preview,
                        width_px: size.w, height_px: size.h, bleed_px: BLEED_PX, safe_zone_px: SAFE_PX, dpi: 300 };
      if (designId) { await api.patch(`/designs/${designId}`, payload); toast.success("Design updated"); }
      else { const { data } = await api.post("/designs", payload); setDesignId(data.id); toast.success("Design saved"); }
    } catch (e) { logger.error(e); toast.error("Save failed"); } finally { setSaving(false); }
  };
  const exportPDF = () => {
    const dataUrl = fabRef.current.toDataURL({ format: "png", multiplier: 2 });
    const pdf = new jsPDF({ unit: "px", format: [size.w, size.h], hotfixes: ["px_scaling"] });
    pdf.addImage(dataUrl, "PNG", 0, 0, size.w, size.h);
    pdf.save(`${designName.replace(/[^a-z0-9]/gi, "-")}.pdf`);
    toast.success("PDF downloaded");
  };
  const goToOrder = async () => {
    await save();
    nav(`/product/${productSlug}?design=${designId || ""}`);
  };

  const activeText = selected && selected.type === "textbox";

  return (
    <section className="max-w-[1400px] mx-auto px-6 py-8" data-testid="designer-root">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <p className="text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">Product Designer</p>
          <input value={designName} onChange={e => setDesignName(e.target.value)} data-testid="design-name"
            className="font-serif text-3xl bg-transparent border-b border-[#E5E5E5] focus:border-[#C5A059] outline-none" />
          {product && <span className="ml-3 text-xs font-mono text-[#888]">for {product.name}</span>}
        </div>
        <div className="flex gap-2">
          <button onClick={undo} data-testid="design-undo" className="px-3 py-2 border border-[#E5E5E5] text-sm"><Undo2 size={14}/></button>
          <button onClick={redo} data-testid="design-redo" className="px-3 py-2 border border-[#E5E5E5] text-sm"><Redo2 size={14}/></button>
          <button onClick={exportPDF} data-testid="design-export-pdf" className="px-4 py-2 border border-[#0a0a0a] text-xs tracking-[0.25em] uppercase font-bold inline-flex items-center gap-2"><Download size={14}/> Export PDF</button>
          <button onClick={save} disabled={saving} data-testid="design-save" className="px-6 py-2 bg-[#0a0a0a] text-white text-xs tracking-[0.25em] uppercase font-bold inline-flex items-center gap-2 hover:bg-[#C5A059] hover:text-[#0a0a0a]"><Save size={14}/> {saving ? "Saving..." : "Save"}</button>
          <button onClick={goToOrder} data-testid="design-goto-order" className="px-6 py-2 bg-[#C5A059] text-[#0a0a0a] text-xs tracking-[0.25em] uppercase font-bold">Add to Order →</button>
        </div>
      </div>
      <div className="grid grid-cols-12 gap-4">
        <aside className="col-span-12 lg:col-span-2 space-y-2 text-sm">
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#C5A059] mb-2">Elements</p>
          <button onClick={addText} data-testid="design-add-text" className="w-full border border-[#E5E5E5] p-2 flex items-center gap-2 hover:border-[#0a0a0a]"><Type size={14}/> Text</button>
          <label className="w-full border border-[#E5E5E5] p-2 flex items-center gap-2 hover:border-[#0a0a0a] cursor-pointer" data-testid="design-add-image-label"><ImgIcon size={14}/> Image
            <input type="file" hidden accept="image/*" onChange={addImage} data-testid="design-add-image"/>
          </label>
          <button onClick={addRect} data-testid="design-add-rect" className="w-full border border-[#E5E5E5] p-2 flex items-center gap-2 hover:border-[#0a0a0a]"><Square size={14}/> Rectangle</button>
          <button onClick={addCircle} data-testid="design-add-circle" className="w-full border border-[#E5E5E5] p-2 flex items-center gap-2 hover:border-[#0a0a0a]"><CircleIcon size={14}/> Circle</button>
          <div className="border border-[#E5E5E5] p-2">
            <label className="flex items-center gap-2 text-xs mb-1"><QrCode size={14}/> QR Code</label>
            <input value={qrText} onChange={e => setQrText(e.target.value)} data-testid="design-qr-text" className="w-full border border-[#E5E5E5] px-2 py-1 text-xs"/>
            <button onClick={addQr} data-testid="design-add-qr" className="mt-2 w-full bg-[#0a0a0a] text-white text-[10px] tracking-widest uppercase py-2">Insert QR</button>
            <div id="qr-tmp" style={{position:"absolute",left:-9999,top:-9999}}><QRCodeCanvas value={qrText} size={200}/></div>
          </div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#C5A059] mt-4 mb-2">Canvas</p>
          <div className="flex gap-1"><input type="number" value={size.w} onChange={e => setSize(s => ({...s, w: Number(e.target.value)||1050}))} data-testid="design-w" className="w-1/2 border border-[#E5E5E5] px-2 py-1 text-xs"/><input type="number" value={size.h} onChange={e => setSize(s => ({...s, h: Number(e.target.value)||600}))} data-testid="design-h" className="w-1/2 border border-[#E5E5E5] px-2 py-1 text-xs"/></div>
          <label className="flex items-center gap-2 text-xs">Bg: <input type="color" value={bg} onChange={e => setBg(e.target.value)} data-testid="design-bg"/></label>
        </aside>
        <div className="col-span-12 lg:col-span-8">
          <div className="border border-[#E5E5E5] bg-[#FAFAFA] p-4 overflow-auto flex justify-center">
            <div style={{position:"relative"}}>
              <canvas ref={canvasRef} data-testid="design-canvas" style={{border:"1px solid #ddd"}}/>
              {/* Bleed + safe zone overlays */}
              <div style={{position:"absolute", inset:0, pointerEvents:"none",
                           border:`${BLEED_PX}px dashed rgba(154,31,31,0.35)`}} title="Bleed"/>
              <div style={{position:"absolute", inset:BLEED_PX+SAFE_PX+"px",
                           border:"1px dashed rgba(31,77,42,0.5)", pointerEvents:"none"}} title="Safe zone"/>
            </div>
          </div>
          <p className="text-[10px] font-mono text-[#888] mt-2">Red = bleed · Green = safe zone · 300 DPI · CMYK will be applied during PDF pre-press</p>
        </div>
        <aside className="col-span-12 lg:col-span-2 space-y-2 text-sm">
          <p className="text-[10px] font-mono uppercase tracking-widest text-[#C5A059] mb-2">Selection</p>
          {selected ? (
            <>
              {activeText && (
                <>
                  <input value={selected.text} onChange={e => { selected.set("text", e.target.value); fabRef.current.renderAll(); }} data-testid="design-text-value" className="w-full border border-[#E5E5E5] px-2 py-1 text-xs"/>
                  <input type="number" value={selected.fontSize} onChange={e => { selected.set("fontSize", Number(e.target.value)); fabRef.current.renderAll(); }} className="w-full border border-[#E5E5E5] px-2 py-1 text-xs"/>
                </>
              )}
              <input type="color" value={selected.fill} onChange={e => { selected.set("fill", e.target.value); fabRef.current.renderAll(); pushHistory(); }} data-testid="design-fill"/>
              <div className="flex gap-1">
                <button onClick={() => align("left")} className="flex-1 border border-[#E5E5E5] p-1"><AlignLeft size={12}/></button>
                <button onClick={() => align("center")} className="flex-1 border border-[#E5E5E5] p-1"><AlignCenter size={12}/></button>
                <button onClick={() => align("right")} className="flex-1 border border-[#E5E5E5] p-1"><AlignRight size={12}/></button>
              </div>
              <div className="flex gap-1">
                <button onClick={bringForward} className="flex-1 border border-[#E5E5E5] p-1 text-xs">↑ Front</button>
                <button onClick={sendBackward} className="flex-1 border border-[#E5E5E5] p-1 text-xs">↓ Back</button>
              </div>
              <button onClick={del} data-testid="design-delete" className="w-full border border-[#9A1F1F] text-[#9A1F1F] p-2 text-xs tracking-widest uppercase flex items-center justify-center gap-2"><Trash2 size={12}/> Delete</button>
            </>
          ) : <p className="text-xs text-[#888]">Select an element to edit.</p>}
          <div className="border-t border-[#E5E5E5] pt-4 mt-4 text-[10px] font-mono text-[#888] leading-relaxed">
            <p className="uppercase tracking-widest text-[#C5A059] mb-1">Print-ready?</p>
            <p>• Keep important content inside the green safe zone</p>
            <p>• Fill artwork past the red bleed line</p>
            <p>• Use high-res images (300 DPI)</p>
            <p>• Files convert to CMYK during pre-press</p>
          </div>
        </aside>
      </div>
    </section>
  );
}
